/**
 * Market data cache client. Reads 1m OHLC from chart memory or IndexedDB (worker).
 */

import type {
  AggregateBarsOptions,
  BarsRange,
  BarsRevisionListener,
  ExecuteReadOnlyOptions,
  InsertBarsPayload,
  OHLCData,
  QueryBarsOptions,
  SqlResult,
  TimeFrame,
} from "./types";
import {
  aggregateOHLCData,
  getTimeframeIntervalMs,
  needsJsAggregation,
} from "./aggregation";
import { executeInMemorySql } from "@/lib/agent/analytics/sql-inmemory";
import { validateReadOnlySql, SqlGuardError } from "./sql-guard";
import { makeSymbolRevisionKey } from "./account-key";
import {
  barsRangeFromSeries,
  loadBarsForSymbol,
  loadIdbBarsForSymbol,
} from "./idb-read";
import { compute1mLoadWindow } from "./window";

function getTzOffsetMs(): number {
  return -new Date().getTimezoneOffset() * 60_000;
}

function filterAndLimitBars(
  bars: OHLCData[],
  opts: QueryBarsOptions,
): OHLCData[] {
  let filtered = bars;
  if (opts.fromTs != null) {
    filtered = filtered.filter((b) => b.timestamp >= opts.fromTs!);
  }
  if (opts.toTs != null) {
    filtered = filtered.filter((b) => b.timestamp <= opts.toTs!);
  }
  if (opts.beforeTs != null) {
    filtered = filtered.filter((b) => b.timestamp < opts.beforeTs!);
  }

  const sorted =
    opts.beforeTs != null
      ? [...filtered].sort((a, b) => b.timestamp - a.timestamp)
      : [...filtered].sort((a, b) => a.timestamp - b.timestamp);

  const limited =
    opts.limit != null
      ? sorted.slice(0, Math.max(1, Math.floor(opts.limit)))
      : sorted;

  if (opts.beforeTs != null) {
    limited.sort((a, b) => a.timestamp - b.timestamp);
  }
  return limited;
}

function aggregateBarsInMemory(
  bars1m: OHLCData[],
  timeframe: TimeFrame,
  opts: AggregateBarsOptions,
): OHLCData[] {
  if (timeframe === "1m") {
    return filterAndLimitBars(bars1m, opts);
  }

  if (needsJsAggregation(timeframe)) {
    const limit = opts.limit ?? 200;
    const raw = filterAndLimitBars(bars1m, {
      ...opts,
      limit: limit * 43200,
    });
    const aggregated = aggregateOHLCData(raw, timeframe);
    return aggregated.slice(-limit);
  }

  const intervalMs = getTimeframeIntervalMs(timeframe);
  if (!intervalMs) {
    return filterAndLimitBars(bars1m, opts);
  }

  const tzOffset = getTzOffsetMs();
  const buckets = new Map<number, OHLCData>();

  for (const bar of bars1m) {
    if (opts.fromTs != null && bar.timestamp < opts.fromTs) continue;
    if (opts.toTs != null && bar.timestamp > opts.toTs) continue;
    if (opts.beforeTs != null && bar.timestamp >= opts.beforeTs) continue;

    const bucketTs =
      Math.floor((bar.timestamp + tzOffset) / intervalMs) * intervalMs -
      tzOffset;

    const existing = buckets.get(bucketTs);
    if (!existing) {
      buckets.set(bucketTs, { ...bar, timestamp: bucketTs });
    } else {
      existing.high = Math.max(existing.high, bar.high);
      existing.low = Math.min(existing.low, bar.low);
      existing.close = bar.close;
      existing.volume += bar.volume;
    }
  }

  let result = Array.from(buckets.values());
  if (opts.beforeTs != null) {
    result = result.filter((b) => b.timestamp < opts.beforeTs!);
    result.sort((a, b) => b.timestamp - a.timestamp);
    if (opts.limit != null) {
      result = result.slice(0, Math.max(1, Math.floor(opts.limit)));
    }
    result.sort((a, b) => a.timestamp - b.timestamp);
    return result;
  }

  result.sort((a, b) => a.timestamp - b.timestamp);
  if (opts.fromTs != null) {
    result = result.filter((b) => b.timestamp >= opts.fromTs!);
  }
  if (opts.limit != null) {
    result = result.slice(0, Math.max(1, Math.floor(opts.limit)));
  }
  return result;
}

class MarketDataDb {
  private initPromise: Promise<void> | null = null;
  private barCache = new Map<string, OHLCData[]>();
  private revisionListeners = new Set<BarsRevisionListener>();
  private symbolRevisions = new Map<string, number>();

  private cacheKey(accountKey: string, symbol: string): string {
    return makeSymbolRevisionKey(accountKey, symbol);
  }

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = Promise.resolve();
    return this.initPromise;
  }

  subscribe(listener: BarsRevisionListener): () => void {
    this.revisionListeners.add(listener);
    return () => {
      this.revisionListeners.delete(listener);
    };
  }

  getRevision(accountKey: string, symbol: string): number {
    return this.symbolRevisions.get(this.cacheKey(accountKey, symbol)) ?? 0;
  }

  private async loadBars(
    accountKey: string,
    symbol: string,
    window?: { fromTs: number; toTs: number },
  ): Promise<OHLCData[]> {
    const key = window
      ? `${this.cacheKey(accountKey, symbol)}:${window.fromTs}:${window.toTs}`
      : this.cacheKey(accountKey, symbol);
    const cached = this.barCache.get(key);
    if (cached) return cached;

    const bars = window
      ? await loadBarsForSymbol(accountKey, symbol, window)
      : await loadIdbBarsForSymbol(accountKey, symbol);

    this.barCache.set(key, bars);
    return bars;
  }

  /**
   * Single load + aggregate for MarketData.Get (avoids full-history scans).
   */
  async getBarsAroundAnchor(
    accountKey: string,
    symbol: string,
    timeframe: TimeFrame,
    alignedAnchorMs: number,
    beforeCount: number,
    afterCount: number,
  ): Promise<{
    before: OHLCData[];
    after: OHLCData[];
    availableRange: BarsRange | null;
  }> {
    await this.init();
    const window = compute1mLoadWindow(
      alignedAnchorMs,
      timeframe,
      beforeCount,
      afterCount,
    );
    const bars1m = await this.loadBars(accountKey, symbol, window);

    const before =
      beforeCount > 0
        ? aggregateBarsInMemory(bars1m, timeframe, {
            beforeTs: alignedAnchorMs,
            limit: beforeCount,
          })
        : [];
    const after =
      afterCount > 0
        ? aggregateBarsInMemory(bars1m, timeframe, {
            fromTs: alignedAnchorMs,
            limit: afterCount,
          })
        : [];

    const range = barsRangeFromSeries(bars1m);
    return {
      before,
      after,
      availableRange: range
        ? { minTs: range.minTs, maxTs: range.maxTs, count: range.count }
        : null,
    };
  }

  async insertBars(payload: InsertBarsPayload): Promise<number> {
    await this.init();
    const key = this.cacheKey(payload.accountKey, payload.symbol);
    const existing = this.barCache.get(key) ?? [];
    const map = new Map<number, OHLCData>();
    for (const b of existing) map.set(b.timestamp, b);
    for (const b of payload.bars) map.set(b.timestamp, b);
    const merged = Array.from(map.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );
    this.barCache.set(key, merged);
    const next = this.getRevision(payload.accountKey, payload.symbol) + 1;
    this.symbolRevisions.set(key, next);
    return next;
  }

  async queryBars(
    accountKey: string,
    symbol: string,
    opts: QueryBarsOptions = {},
  ): Promise<OHLCData[]> {
    await this.init();
    const bars = await this.loadBars(accountKey, symbol);
    return filterAndLimitBars(bars, opts);
  }

  async aggregateBars(
    accountKey: string,
    symbol: string,
    timeframe: TimeFrame,
    opts: AggregateBarsOptions = {},
  ): Promise<OHLCData[]> {
    await this.init();
    let window: { fromTs: number; toTs: number } | undefined;
    if (opts.fromTs != null && opts.toTs != null) {
      const margin = 24 * 60 * 60_000;
      window = {
        fromTs: opts.fromTs - margin,
        toTs: opts.toTs + margin,
      };
    } else if (opts.beforeTs != null || opts.fromTs != null) {
      const anchor = opts.fromTs ?? opts.beforeTs ?? Date.now();
      const limit = opts.limit ?? 100;
      window = compute1mLoadWindow(anchor, timeframe, limit, limit);
    }
    const bars = await this.loadBars(accountKey, symbol, window);
    return aggregateBarsInMemory(bars, timeframe, opts);
  }

  /** Drop in-memory bar cache for a symbol (after IDB prefetch). */
  async invalidateSymbolCache(
    accountKey: string,
    symbol: string,
  ): Promise<void> {
    await this.init();
    const prefix = `${this.cacheKey(accountKey, symbol)}`;
    for (const key of [...this.barCache.keys()]) {
      if (key.startsWith(prefix)) this.barCache.delete(key);
    }
  }

  /** Drop all in-memory bar cache entries for an account (company_server). */
  async invalidateAccountCache(accountKey: string): Promise<void> {
    await this.init();
    const prefix = `${accountKey}|`;
    for (const key of [...this.barCache.keys()]) {
      if (key.startsWith(prefix)) this.barCache.delete(key);
    }
  }

  async getRange(
    accountKey: string,
    symbol: string,
  ): Promise<BarsRange | null> {
    await this.init();
    const bars = await this.loadBars(accountKey, symbol);
    const range = barsRangeFromSeries(bars);
    if (!range) return null;
    return {
      minTs: range.minTs,
      maxTs: range.maxTs,
      count: range.count,
    };
  }

  async executeReadOnly(
    sql: string,
    opts: ExecuteReadOnlyOptions,
  ): Promise<SqlResult> {
    await this.init();
    const started = performance.now();
    try {
      validateReadOnlySql(sql);
    } catch (err) {
      if (err instanceof SqlGuardError) {
        throw new Error(err.message);
      }
      throw err;
    }

    const bars = await this.loadBarsFromSql(sql);
    const result = executeInMemorySql(sql, bars, opts.rowCap);

    return {
      columns: result.columns,
      rows: result.rows,
      rowCount: result.rowCount,
      truncated: result.truncated,
      durationMs: performance.now() - started,
    };
  }

  private async loadBarsFromSql(sql: string): Promise<OHLCData[]> {
    const symbolMatch = sql.match(/symbol\s*=\s*'([^']+)'/i);
    const accountMatch = sql.match(/account_key\s*=\s*'([^']+)'/i);
    if (!symbolMatch) {
      throw new Error(
        "MarketData.Sql: unsupported query — include symbol = 'PAIR' filter",
      );
    }
    const symbol = symbolMatch[1]!;

    const { loadBarsFromChartStore, loadIdbBarsForSymbol } = await import(
      "./idb-read"
    );
    const { resolveActiveAccountKey } = await import("./resolve-active-account");

    const fromChart = await loadBarsFromChartStore(symbol);
    if (fromChart && fromChart.length > 0) return fromChart;

    if (accountMatch?.[1]) {
      const keyed = await this.loadBars(accountMatch[1], symbol);
      if (keyed.length > 0) return keyed;
    }

    try {
      const activeKey = await resolveActiveAccountKey();
      const fromIdb = await loadIdbBarsForSymbol(activeKey, symbol);
      if (fromIdb.length > 0) return fromIdb;
    } catch {
      /* no active account */
    }

    throw new Error(
      `MarketData.Sql: no cached bars for symbol '${symbol}'. Open the symbol on a chart or run Watchlist.Cache(symbol, from, to).`,
    );
  }
}

export const marketDataDb = new MarketDataDb();

export function initializeMarketDataDb(): void {
  void marketDataDb.init().catch(() => {
    /* logged inside init */
  });
}
